import React, { PropsWithChildren, useEffect, useRef } from 'react'
import Flickity from 'react-flickity-component'
import { FlickityRefObject } from '../../types/FlickityRefObject'
import {
  StyleCta,
  StyledCarouselImageWrapper,
  StyledContentSection,
  StyledDescription,
  StyledImage,
  StyledOfferPromotionCarouselContainer,
  StyledOfferPromotionCarouselSection,
  StyledOfferPromotionCarouselWrapper,
  StyledTitle,
} from './styledCarouselElements'
import { StyledFlickityContentComponent, StyledFlickityImageComponent } from './styledFlickityCarousel'

export const DEFAULT_CAROUSEL_HEIGHT = {
  mobile: {
    image: 209,
    content: 320,
  },
  desktop: {
    image: 583,
    content: 583,
  },
}

// Image
export type CarouselImageProps = React.ComponentProps<typeof StyledImage> & {
  src?: string
}

export const CarouselImage: React.FC<PropsWithChildren<CarouselImageProps>> = ({ src, ...props }) => (
  <StyledCarouselImageWrapper item xs={12} sm={12}>
    <StyledImage src={src} {...props} />
  </StyledCarouselImageWrapper>
)

// Title
export type CarouselTitleProps = React.ComponentProps<typeof StyledTitle> & {
  //
}

export const CarouselTitle: React.FC<PropsWithChildren<CarouselTitleProps>> = ({ children, ...props }) => (
  <StyledTitle {...props}>{children}</StyledTitle>
)

// Description
export type CarouselDescriptionProps = React.ComponentProps<typeof StyledDescription> & {
  //
}

export const CarouselDescription: React.FC<PropsWithChildren<CarouselDescriptionProps>> = ({ href, children, ...props }) => (
  <StyledDescription {...props}>{children}</StyledDescription>
)

// Cta
export type CarouselCtaProps = React.ComponentProps<typeof StyleCta> & {
  href?: string | undefined
  target?: string | undefined
}

export const CarouselCta: React.FC<PropsWithChildren<CarouselCtaProps>> = ({ href, target, children, ...props }) => (
  <StyleCta href={href} target={target === '_blank' ? '_blank' : '_self'} {...props}>{children}</StyleCta>
)

// Content section
export type ContentSectionProps = React.ComponentProps<typeof StyledContentSection> & {
  layout?: 'vertical' | 'horizontal'
}

export const ContentSection: React.FC<PropsWithChildren<ContentSectionProps>> = ({ layout, children, ...props }) => (
  <StyledContentSection {...props}
    container
    direction="column"
    wrap="nowrap"
    alignItems={'flex-start'}
    justifyContent={'flex-start'}>
    {children}
  </StyledContentSection>
)

// Carousel item container
export type OfferPromotionCarouselSectionProps = React.ComponentProps<typeof StyledOfferPromotionCarouselSection> & {
  layout?: 'vertical' | 'horizontal'
  variant?: 'offer' | 'promotion'
}

export const OfferPromotionCarouselSection: React.FC<PropsWithChildren<OfferPromotionCarouselSectionProps>> = ({ layout, variant, children, ...props }) => {
  if (layout === 'vertical') {
    return (
      <StyledOfferPromotionCarouselSection {...props}
        container
        direction="column"
        justifyContent="center"
        alignItems="center">
        {children}
      </StyledOfferPromotionCarouselSection>
    )
  }

  return (
    <StyledOfferPromotionCarouselSection {...props}
      container
      direction={variant === 'offer' ? 'row' : 'row-reverse'}
      justifyContent="flex-start"
      alignItems="flex-start"
      wrap="nowrap">
      {children}
    </StyledOfferPromotionCarouselSection>
  )
}

// Carousel wrapper
export type OfferPromotionCarouselWrapperProps = React.ComponentProps<typeof StyledOfferPromotionCarouselWrapper> & {
  layout?: 'vertical' | 'horizontal'
}

export const OfferPromotionCarouselWrapper: React.FC<PropsWithChildren<OfferPromotionCarouselWrapperProps>> = ({ layout = 'vertical', children, ...props }) => {
  if (layout === 'vertical') {
    return (
      <StyledOfferPromotionCarouselWrapper {...props}
        container
        direction="column"
        justifyContent="center"
        alignItems="center">
        <FlickityComponent layout={layout}>
          {children}
        </FlickityComponent>
      </StyledOfferPromotionCarouselWrapper>
    )
  }

  // Filter all offer items
  const offers = React.Children.toArray(children).map(item => {
    if (React.isValidElement(item)) {
      const carouselItem = React.cloneElement(item as React.ReactElement<OfferPromotionCarouselSectionProps>)

      if (carouselItem.props.variant === 'offer') {
        return carouselItem
      }
    }
  }).filter(x => x!== undefined)

  // Then filter promotion items
  const promotions = React.Children.toArray(children).map(item => {
    if (React.isValidElement(item)) {
      const carouselItem = React.cloneElement(item as React.ReactElement<OfferPromotionCarouselSectionProps>)

      if (carouselItem.props.variant === 'promotion') {
        return carouselItem
      }
    }
  }).filter(x => x!== undefined)

  return (
    <StyledOfferPromotionCarouselWrapper {...props}
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start">
      {offers && offers.length > 0 && <FlickityComponent variant="offer" layout={layout}>
        {offers}
      </FlickityComponent>}
      {promotions && promotions.length > 0 && <FlickityComponent variant="promotion" layout={layout}>
        {promotions}
      </FlickityComponent>}
    </StyledOfferPromotionCarouselWrapper>
  )
}

// Flickity Component
type FlickityComponentProps = Omit<React.ComponentProps<typeof Flickity>, 'flickityRef'> & {
  variant?: 'offer' | 'promotion'
  layout: 'vertical' | 'horizontal'
}

const FlickityComponent: React.FC<PropsWithChildren<FlickityComponentProps>> = ({ variant, layout, children, ...props }) => {
  const flickityRefImage = useRef(null) as FlickityRefObject
  const flickityRefContent = useRef(null) as FlickityRefObject
  const flickityImageHeight = layout === 'vertical' ? DEFAULT_CAROUSEL_HEIGHT.mobile.image : DEFAULT_CAROUSEL_HEIGHT.desktop.image


  // Handle change other flickty content if flickty image change (and vice versa)
  useEffect(() => {
    const imageInstance = flickityRefImage.current?.flkty
    const contentInstance = flickityRefContent.current?.flkty

    if (imageInstance) {
      setTimeout(() => {
        imageInstance.select(0)
      } , 200)

      imageInstance.on('change', (index: number) => {
        if (contentInstance) contentInstance.select(index)
      })
    }

    if (contentInstance) {
      setTimeout(() => {
        contentInstance.select(0)
      } , 200)
      contentInstance.on('change', (index: number) => {
        if (imageInstance) imageInstance.select(index)
      })
    }

    setTimeout(() => {
      if (imageInstance) {
        imageInstance.select(0)
      }
    } , 200)
  }, [])

  return (
    <StyledOfferPromotionCarouselContainer $variant={variant}>
      <StyledFlickityImageComponent ref={flickityRefImage} $height={flickityImageHeight} {...props} options={{
        cellAlign: 'left',
        pageDots: false,
        initialIndex: 0,
        prevNextButtons: false,
      }}>
        {
          React.Children.toArray(children).map((section, index) => {
            const flickitySection = React.cloneElement(section as React.ReactElement<OfferPromotionCarouselSectionProps>)
            
            return (
              <React.Fragment key={index}>
                {
                  flickitySection.props.children.map(content => {
                    if (content?.props?.image === true) {
                      return content
                    }

                    return null
                  })
                }
              </React.Fragment>
            )
          })
        }
      </StyledFlickityImageComponent>
      <StyledFlickityContentComponent ref={flickityRefContent} {...props} options={{
        cellAlign: 'left',
        pageDots: React.Children.toArray(children).length > 1,
        prevNextButtons: React.Children.toArray(children).length > 1,
        arrowShape: 'm 97.38829,49.006339 c 0,3.03516 -2.385,5.41992 -5.2032,5.41992 H 18.47401 l 28.83402,27.31668 c 2.16798,1.9512 2.16798,5.4198 0.21678,7.371 -1.9512,2.1678 -5.20314,2.1678 -7.37112,0.2166 L 1.99747,52.908679 c -1.083984,-1.08396 -1.51758,-2.38476 -1.51758,-3.90234 0,-1.3008 0.43362,-2.60154 1.51758,-3.68556 L 40.15369,8.8989189 c 2.16798,-1.951176 5.41992,-1.951176 7.37112,0.21678 1.9512,1.9511761 1.9512,5.4199081 -0.21678,7.3711081 l -28.83402,27.31638 h 73.71108 c 3.0348,0 5.2032,2.38476 5.2032,5.20314 z',
        initialIndex: 0,
      }}>
        {
          React.Children.toArray(children).map((section, index) => {
            const flickitySection = React.cloneElement(section as React.ReactElement<OfferPromotionCarouselSectionProps>)
            
            return (
              <React.Fragment key={index}>
                {
                  flickitySection.props.children.map(content => {
                    if (content?.props?.contentSection === true) {
                      return content
                    }
                    
                    return null
                  })
                }
              </React.Fragment>
            )
          })
        }
      </StyledFlickityContentComponent>
    </StyledOfferPromotionCarouselContainer>
  )
}